import React from 'react';
import { useSelector } from 'react-redux';
import { useTransferAccountConfirmNumber } from './useTransferAccountConfirmNumber';
import { StatusScreen } from '../../StatusScreen/StatusScreen';
import { TransferAccountDTO } from '../../../api/useTransferAccountsOptionsQuery';
import { ModalHeader } from '../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalBody } from '../../../../../CommonComponents/OPL/Modal/ModalBody';
import { Modal } from '../../../../../CommonComponents/OPL/Modal/Modal';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { ModalFooter } from '../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Input } from '../../../../../CommonComponents/OPL/Input/Input';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { Alert } from '../../../../../CommonComponents/OPL/Alert/Alert';
import { useNavigateToTransactions } from '../../../hooks/useNavigateToTransactions';
import { showTransactionsSelector } from '../../../../../CommonSelectors/CommonAreaSelector';

interface Props {
  onClose(): void;
  selectedAccount: TransferAccountDTO;
}

export const TransferAccountConfirmNumber: React.FC<Props> = ({ onClose, selectedAccount }) => {
  const { data, actions } = useTransferAccountConfirmNumber({
    selectedAccountId: selectedAccount.id,
  });

  const showTransactions = useSelector(showTransactionsSelector);

  const { navigateToTransactions } = useNavigateToTransactions();

  const {
    formBag: {
      formState: { errors },
      register,
      handleSubmit,
    },
    showSuccessScreen,
    initiateAccountTransferMutation,
  } = data;
  const { onSubmit } = actions;
  const {
    isLoading: isInitiatingAccountTransfer,
    error: initiatingAccountTransferError,
  } = initiateAccountTransferMutation;

  if (showSuccessScreen) {
    return (
      <StatusScreen
        title="Transfer Submitted"
        status="success"
        action={
          showTransactions
            ? { label: 'View Pending Transaction', onClick: navigateToTransactions }
            : undefined
        }
        onClose={onClose}
      >
        <>
          {showTransactions
            ? 'Pending transactions will show in the transactions page of your account. '
            : ''}
          <>
            The transfer process will take approximately&nbsp;
            <Text fontWeight={600} color="neutral.600" as="span">
              7 &mdash; 10 business days.
            </Text>
          </>
        </>
      </StatusScreen>
    );
  }

  return (
    <Modal isOpen onRequestClose={onClose}>
      <ModalHeader>Confirm External Account Number</ModalHeader>
      <ModalCloseButton onClick={onClose} />

      {initiatingAccountTransferError && (
        <Alert message={initiatingAccountTransferError.message} variant="error" my="16" mx="24" />
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          Please enter your external account number to proceed with the transfer.
          <Box width={280}>
            <Input
              label="Account Number"
              mt="16"
              formVariant={errors.accountNumber ? 'error' : undefined}
              helperText={errors.accountNumber?.message}
              {...register('accountNumber')}
            />
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button variant="subtleInactive" onClick={onClose} mr="8">
            Cancel
          </Button>

          <Button type="submit" isLoading={isInitiatingAccountTransfer}>
            Transfer
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
